import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { X, History, User, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import BaseModal from '../BaseModal'
import AuditApiService, { AuditLogEntry } from '../../services/AuditApiService'

const ModalCard = styled.div`
  background: #ffffff;
  width: 90vw;
  max-width: 800px;
  max-height: 85vh;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
`

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666666;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    color: #1a1a1a;
  }
`

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 16px;
    bottom: 16px;
    left: 20px;
    width: 2px;
    background: #e5e5e5;
  }
`

const TimelineItem = styled.div`
  position: relative;
  padding-left: 48px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TimelineDot = styled.div<{ $action: string }>`
  position: absolute;
  left: 12px;
  top: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $action }) => {
    switch ($action) {
      case 'RESTORE':
        return '#0d9488'
      case 'INSERT':
        return '#10b981'
      case 'DELETE':
        return '#ef4444'
      case 'UPDATE':
      default:
        return '#3b82f6'
    }
  }};
  border: 3px solid #ffffff;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08);
`

const Card = styled.div`
  background: #fdfdfd;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #d1d1d1;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`

const ActionBadge = styled.span<{ $action: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ $action }) => {
    switch ($action) {
      case 'RESTORE':
        return '#ccfbf1'
      case 'INSERT':
        return '#ecfdf5'
      case 'DELETE':
        return '#fef2f2'
      case 'UPDATE':
      default:
        return '#eff6ff'
    }
  }};
  color: ${({ $action }) => {
    switch ($action) {
      case 'RESTORE':
        return '#0f766e'
      case 'INSERT':
        return '#047857'
      case 'DELETE':
        return '#b91c1c'
      case 'UPDATE':
      default:
        return '#1d4ed8'
    }
  }};
`

const DateText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #737373;
`

const DiffToggle = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`

const DiffTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  font-size: 13px;

  th,
  td {
    border: 1px solid #e5e5e5;
    padding: 8px 12px;
    text-align: left;
  }

  th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
  }
`

const OldVal = styled.span`
  color: #dc2626;
  background: #fef2f2;
  padding: 2px 6px;
  border-radius: 4px;
`

const NewVal = styled.span`
  color: #16a34a;
  background: #f0fdf4;
  padding: 2px 6px;
  border-radius: 4px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #8c8c8c;
  font-size: 14px;
`

interface AuditHistoryModalProps {
  tableName: string
  recordId: number
  recordTitle?: string
  onClose: () => void
}

export const AuditHistoryModal: React.FC<AuditHistoryModalProps> = ({
  tableName,
  recordId,
  recordTitle,
  onClose,
}) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({})

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true)
        const data = await AuditApiService.getRecordAuditHistory(tableName, recordId)
        setLogs(data)
      } catch (error) {
        console.error('Erro ao buscar histórico de auditoria:', error)
      } finally {
        setLoading(false)
      }
    }

    if (tableName && recordId) {
      fetchHistory()
    }
  }, [tableName, recordId])

  const toggleExpand = (id: number) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const formatActionLabel = (action: string) => {
    switch (action) {
      case 'INSERT':
        return 'Criação'
      case 'UPDATE':
        return 'Alteração'
      case 'DELETE':
        return 'Exclusão'
      case 'RESTORE':
        return 'Restauração'
      default:
        return action
    }
  }

  return (
    <BaseModal>
      <ModalCard>
        <ModalHeader>
          <TitleGroup>
            <History size={20} color="#2563eb" />
            <div>
              <HeaderTitle>Auditoria do Registro</HeaderTitle>
              <div style={{ fontSize: 12, color: '#737373', marginTop: 2 }}>
                {recordTitle ? `${recordTitle} • ` : ''}Tabela: {tableName} • ID: {recordId}
              </div>
            </div>
          </TitleGroup>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <EmptyState>Carregando histórico de auditoria...</EmptyState>
          ) : logs.length === 0 ? (
            <EmptyState>Nenhum registro de auditoria encontrado para este item.</EmptyState>
          ) : (
            <TimelineContainer>
              {logs.map((log) => {
                const isExpanded = !!expandedItems[log.id]
                const isDelete = log.action === 'DELETE'
                const internalMetadataKeys = [
                  'restored',
                  'restoredAt',
                  'restoredById',
                  'restoredRecordId',
                  'newRecordId',
                  'originalLogId',
                  'restoredFromId',
                  'previousRecordId',
                ]

                const rawRestoredFromId =
                  log.restoredFromId ||
                  (typeof (log.diff as any)?.restoredFromId === 'object'
                    ? (log.diff as any)?.restoredFromId?.new || (log.diff as any)?.restoredFromId?.old
                    : (log.diff as any)?.restoredFromId) ||
                  (typeof (log.diff as any)?.previousRecordId === 'object'
                    ? (log.diff as any)?.previousRecordId?.new || (log.diff as any)?.previousRecordId?.old
                    : (log.diff as any)?.previousRecordId) ||
                  (log.newValue as any)?.restoredFromId

                const restoredFromId =
                  rawRestoredFromId && typeof rawRestoredFromId !== 'object'
                    ? String(rawRestoredFromId)
                    : null

                const diffEntries =
                  !isDelete && log.diff && typeof log.diff === 'object'
                    ? Object.entries(log.diff).filter(
                        ([k, v]: [string, any]) =>
                          !internalMetadataKeys.includes(k) &&
                          v !== null &&
                          typeof v === 'object' &&
                          (v.old !== undefined || v.new !== undefined) &&
                          !(v.old === undefined && v.new === undefined)
                      )
                    : []

                return (
                  <TimelineItem key={log.id}>
                    <TimelineDot $action={log.action} />
                    <Card>
                      <CardHeader>
                        <UserInfo>
                          <User size={16} color="#6b7280" />
                          <span>{log.ChangedBy?.name || 'Sistema / Desconhecido'}</span>
                          {log.ChangedBy?.email && (
                            <span style={{ color: '#9ca3af', fontSize: 12 }}>
                              ({log.ChangedBy.email})
                            </span>
                          )}
                        </UserInfo>
                        <ActionBadge $action={log.action}>
                          {formatActionLabel(log.action)}
                        </ActionBadge>
                      </CardHeader>

                      <DateText>
                        <Clock size={14} />
                        {new Date(log.changedAt).toLocaleString('pt-BR')}
                      </DateText>

                      {log.action === 'RESTORE' && (
                        <div
                          style={{
                            fontSize: 12,
                            padding: '6px 10px',
                            borderRadius: 6,
                            background: '#f0fdfa',
                            color: '#0f766e',
                            border: '1px solid #ccfbf1',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 2,
                          }}
                        >
                          <span>
                            Registro restaurado a partir do item original
                            {restoredFromId ? ` (#${restoredFromId})` : ''}
                          </span>
                        </div>
                      )}

                      {diffEntries.length > 0 && (
                        <div>
                          <DiffToggle onClick={() => toggleExpand(log.id)}>
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            {isExpanded
                              ? 'Ocultar campos alterados'
                              : `Ver ${diffEntries.length} campo(s) alterado(s)`}
                          </DiffToggle>

                          {isExpanded && (
                            <DiffTable>
                              <thead>
                                <tr>
                                  <th>Campo</th>
                                  <th>Valor Anterior</th>
                                  <th>Novo Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {diffEntries.map(([field, values]: [string, any]) => {
                                  const fieldLabel = values?.label || field;
                                  const oldDisplay = values?.old !== null && values?.old !== undefined ? String(values.old) : '—';
                                  const newDisplay = values?.new !== null && values?.new !== undefined ? String(values.new) : '—';

                                  return (
                                    <tr key={field}>
                                      <td>
                                        <strong>{fieldLabel}</strong>
                                        {values?.isAutoCalculated && (
                                          <span
                                            style={{
                                              marginLeft: 8,
                                              fontSize: 11,
                                              padding: '2px 6px',
                                              borderRadius: 4,
                                              background: '#eff6ff',
                                              color: '#2563eb',
                                              fontWeight: 500,
                                              border: '1px solid #dbeafe',
                                            }}
                                          >
                                            Cálculo automático
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <OldVal>{oldDisplay}</OldVal>
                                      </td>
                                      <td>
                                        <NewVal>{newDisplay}</NewVal>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </DiffTable>
                          )}
                        </div>
                      )}
                    </Card>
                  </TimelineItem>
                )
              })}
            </TimelineContainer>
          )}
        </ModalBody>
      </ModalCard>
    </BaseModal>
  )
}

export default AuditHistoryModal
